import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	OnDestroy,
	OnInit,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { NzMessageService } from 'ng-zorro-antd/message';

import {
	BasePageComponent,
	PageSkeletonType,
} from '@app/abstracts/base-page/base-page.component';
import { PageInterface, PageService } from '@app/services/page.service';
import { ErrorService } from '@app/services/error.service';
import { NavigationService } from '@app/services/navigation.service';

import {
	SignatureRemovalRequest,
	SignatureRemovalRequestService,
	SignatureRemovalRequestStatus,
} from '@app/models/signature-removal-request';

@Component({
	selector: 'app-molecule-admin-signature-removal-request-edit',
	templateUrl:
		'./molecule-admin-signature-removal-request-edit.component.html',
	styleUrls: [
		'./molecule-admin-signature-removal-request-edit.component.less',
	],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MoleculeAdminSignatureRemovalRequestEditComponent
	extends BasePageComponent
	implements OnInit, OnDestroy
{
	protected skeleton: PageSkeletonType = 'detail';

	request: SignatureRemovalRequest | null = null;
	loading = false;

	approveModal = false;
	rejectModal = false;
	approveLoading = false;
	rejectLoading = false;

	private id: string | null = null;
	private unsubscribe$ = new Subject<void>();

	constructor(
		protected pageService: PageService,
		protected translate: TranslateService,
		private route: ActivatedRoute,
		private signatureRemovalRequestService: SignatureRemovalRequestService,
		private errorService: ErrorService,
		private navigationService: NavigationService,
		private message: NzMessageService,
		private cdr: ChangeDetectorRef
	) {
		super(pageService, translate);
	}

	ngOnInit(): void {
		super.ngOnInit();

		this.route.params
			.pipe(takeUntil(this.unsubscribe$))
			.subscribe(async (params) => {
				this.id = params.id;
				await this.fetch();
			});
	}

	ngOnDestroy(): void {
		super.ngOnDestroy();
		this.unsubscribe$.next();
		this.unsubscribe$.complete();
	}

	getPageData(): PageInterface {
		return {
			title: this.translate.instant(
				'signature-removal-request_admin-list-title'
			),
			breadcrumbs: [
				{
					label: this.translate.instant(
						'signature-removal-request_admin-list-title'
					),
					link: '/signature-removal-requests/admin/list',
				},
				{
					label: this.translate.instant(
						'signature-removal-request_detail-section-snapshot'
					),
				},
			],
			hideTopBar: false,
			hideMenu: false,
		};
	}

	async fetch(): Promise<void> {
		if (!this.id) return;
		this.loading = true;
		this.cdr.markForCheck();
		try {
			this.request = await this.signatureRemovalRequestService.getAsAdmin(
				this.id,
				{
					_populate: [
						'stagiaire',
						'requester',
						'formation',
						'thematique',
					],
				}
			);
		} catch (err) {
			this.errorService.handle(err);
			this.request = null;
		}
		this.loading = false;
		this.cdr.markForCheck();
	}

	isPending(): boolean {
		return !!this.request && this.request.props.status === 'pending';
	}

	openApproveModal(): void {
		this.approveModal = true;
	}

	closeApproveModal(): void {
		this.approveModal = false;
	}

	openRejectModal(): void {
		this.rejectModal = true;
	}

	closeRejectModal(): void {
		this.rejectModal = false;
	}

	async confirmApprove(): Promise<void> {
		if (!this.id) return;
		this.approveLoading = true;
		this.cdr.markForCheck();
		try {
			await this.signatureRemovalRequestService.approve(this.id);
			this.message.success(
				this.translate.instant(
					'signature-removal-request_approve-success'
				)
			);
			this.approveModal = false;
			this.navigationService.go('/signature-removal-requests/admin/list');
		} catch (err) {
			if (err instanceof HttpErrorResponse && err.status === 409) {
				this.message.warning(
					this.translate.instant(
						'signature-removal-request_already-moderated'
					)
				);
				this.approveModal = false;
				await this.fetch();
			} else {
				this.errorService.handle(err);
			}
		}
		this.approveLoading = false;
		this.cdr.markForCheck();
	}

	async confirmReject(): Promise<void> {
		if (!this.id) return;
		this.rejectLoading = true;
		this.cdr.markForCheck();
		try {
			await this.signatureRemovalRequestService.reject(this.id);
			this.message.success(
				this.translate.instant(
					'signature-removal-request_reject-success'
				)
			);
			this.rejectModal = false;
			this.navigationService.go('/signature-removal-requests/admin/list');
		} catch (err) {
			if (err instanceof HttpErrorResponse && err.status === 409) {
				this.message.warning(
					this.translate.instant(
						'signature-removal-request_already-moderated'
					)
				);
				this.rejectModal = false;
				await this.fetch();
			} else {
				this.errorService.handle(err);
			}
		}
		this.rejectLoading = false;
		this.cdr.markForCheck();
	}

	getStatusColor(status: SignatureRemovalRequestStatus): string {
		switch (status) {
			case 'pending':
				return 'blue';
			case 'approved':
				return 'green';
			case 'rejected':
				return 'red';
			default:
				return 'default';
		}
	}

	getStatusKey(status: SignatureRemovalRequestStatus): string {
		return 'signature-removal-request_status_' + status;
	}

	getPeriodKey(period: 'morning' | 'afternoon'): string {
		return 'signature-removal-request_period_' + period;
	}

	getThematiqueLabel(): string {
		if (!this.request) return '';
		const value: any = this.request.props.thematique;
		if (!value) return '';
		if (typeof value === 'string') return value;
		return value.name || value._id || '';
	}

	getFormationLabel(): string {
		if (!this.request) return '';
		const value: any = this.request.props.formation;
		if (!value) return '';
		if (typeof value === 'string') return value;
		return value.name || value._id || '';
	}

	getStagiaireLabel(): string {
		if (!this.request) return '';
		const value: any = this.request.props.stagiaire;
		if (!value) return '';
		if (typeof value === 'string') return value;
		const fn = value.first_name || '';
		const ln = value.last_name || '';
		const full = (fn + ' ' + ln).trim();
		return full || value._id || '';
	}

	getRequesterLabel(): string {
		if (!this.request) return '';
		const value: any = this.request.props.requester;
		if (!value) return '';
		if (typeof value === 'string') return value;
		const fn = value.first_name || '';
		const ln = value.last_name || '';
		const full = (fn + ' ' + ln).trim();
		return full || value._id || '';
	}

	getDecisionByLabel(): string {
		if (!this.request) return '';
		const value: any = this.request.props.decision_by;
		if (!value) return '';
		if (typeof value === 'string') return value;
		const fn = value.first_name || '';
		const ln = value.last_name || '';
		const full = (fn + ' ' + ln).trim();
		return full || value._id || '';
	}
}
